import React, { useState, useEffect, FunctionComponent } from "react"
import dayjs from "dayjs"
import MBSListTable from "./MBSListTable"
import Spinner from "@murphy-frontend/web-core/components/Spinner"
import { getConvertedDate, compareMonth } from "@murphy-frontend/common/services/TimeService"
import { useGetSendOuts } from "../queries"
import { useModal } from "@murphy-frontend/web-core/contexts/ModalContext"
import { useUserPreferences } from "../../../common/contexts/UserPreferencesContext"
import { ModalConfiguration } from "@murphy-frontend/web-core/components/Modal"
import { useCustomer } from "../../../common/contexts/CustomerContext"
import OptionSet from "@murphy-frontend/web-core/components/OptionSet"
import { alertDropDowns } from "@murphy-frontend/web-core/constants"
import { translateConstantsWithBackendTranslations } from "../../../common/services/translationService"
import ComponentWrapper from "../../../common/components/ComponentWrapper"
import { ContactListPaginated } from "../../Contacts/components/ContactListPaginated"
import ColumnGroupTable from "@murphy-frontend/web-core/components/ColumnGroupTable"
import { createColumnGroup, sortByKey } from "@murphy-frontend/web-core/components/ColumnGroupTable/TableUtils";
import { ColumnGroup } from "@murphy-frontend/web-core/components/ColumnGroupTable/Models"
import { useNavigate } from "react-router-dom"

const MBSList: FunctionComponent = () => {
	const { customer } = useCustomer()
	const { openModal } = useModal()
	const { translations, timeZone } = useUserPreferences()

	const [error, setError] = useState(null);
	const [currentSortingColumn, setCurrentSortingColumn] = useState<string>('name');
	const [pageNum, setPageNum] = useState<number>(1);
	const [sortedAscending, setSortedAscending] = useState<boolean>(true);
	const [columnGroups, setColumnGroups] = useState<ColumnGroup[]>([]);
	const [sendOutBatches, setSendOutBatches] = useState([])
	const [filterValue, setFilterValue] = useState("1")
	const [options, setOptions] = useState(alertDropDowns)
	const navigate = useNavigate();

	const {
		isLoading: isSendOutsLoading,
		isError: isGetSendOutsError,
		data: sendOutsData,
		error: getSendOutsError
	} = useGetSendOuts(customer.Id)

	const ErrorMessage = () => <p>{error}</p>

	useEffect(() => {
		if (translations) {
			const translatedProducts = translateConstantsWithBackendTranslations(alertDropDowns, translations)
			setOptions(translatedProducts)
		}
	}, [translations])

	const onGetSendOutBatchesSuccess = (data: any, tz, filterValue: string) => {
		const mappedData = data.map(p => ({
			username: p.User,
			id: p.SendOutId,
			date: dayjs.utc(p.CreatedDateTimeUtc),
			title: p.Title
		}))

		mappedData.sort((a, b) => b.date - a.date)
		// const mappedDataMonthSort = mappedData.filter(m => compareMonth(m.date, tz, filterValue))
		const newMappedData = mappedData.map(p => ({
			username: p.username,
			id: p.id,
			originalDate: p.date,
			date: getConvertedDate(p.date, tz),
			title: p.title
		}))
		setSendOutBatches(newMappedData)
	}

	const sortByColumn = (columnKey: string) => {
		setCurrentSortingColumn(columnKey);
		const newSortingOrder = !sortedAscending;
		setSortedAscending(newSortingOrder);
	};


	const titleColumnGroup = (data) => {
		const titleColumn = [
			{
				columnKey: "title",
				displayName: translations.title || "Title",
				isSortable: true,
				size: 'column-medium-large-alerts',
				onClickColumn: sortByColumn,
			},
		];
		const titleRows = data.map((row) => ([{
			columnKey: 'title',
			id: `title-${row.ID}`,
			rowId: row.uniqueKey,
			title: row.title,
			displayValue: (
				<div className="vertical-aligner space-between-flex">
					<span>
						{row.title}
					</span>
				</div>
			)
		}]));
		const titleColGroup = createColumnGroup("", titleColumn, titleRows);
		return titleColGroup;
	};

	const dateColumnGroup = (data) => {
		const dateColumn = [
			{
				columnKey: "date",
				displayName: translations.datecreate || "Date Created",
				isSortable: true,
				size: 'column-medium-large-alerts',
				onClickColumn: sortByColumn,
			},
		];
		const dateRows = data.map((row) => ([{
			columnKey: 'date',
			id: `date-${row.ID}`,
			rowId: row.uniqueKey,
			title: row.date,
			displayValue: (
				<div className="vertical-aligner space-between-flex">
					<span>
						{row.date}
					</span>
				</div>
			)
		}]));
		const dateColGroup = createColumnGroup("", dateColumn, dateRows);
		return dateColGroup;
	};


	const creatorColumnGroup = (data) => {
		const creatorColumn = [
			{
				columnKey: "username",
				displayName: translations.creator || "Creator",
				isSortable: true,
				size: 'column-medium-large-alerts',
				onClickColumn: sortByColumn,
			},
		];
		const creatorRows = data.map((row) => ([{
			columnKey: 'username',
			id: `username-${row.ID}`,
			rowId: row.uniqueKey,
			title: row.username,
			displayValue: (
				<div className="vertical-aligner space-between-flex">
					<span>
						{row.username}
					</span>
				</div>
			)
		}]));
		const creatorColGroup = createColumnGroup("", creatorColumn, creatorRows);
		return creatorColGroup;
	};

	const viewresponseColumnGroup = (data) => {
		const viewresponseCol = [
			{
				columnKey: "viewresponse",
				displayName: "",
				isSortable: false,
				size: 'column-medium-large-alerts',
				onClickColumn: sortByColumn,
			},
		];
		const viewresponseRows = data.map((row) => ([{
			columnKey: 'viewresponse',
			id: `viewresponse-${row.ID}`,
			rowId: row.uniqueKey,
			title: 'viewresponse',
			displayValue: (
					<div onClick={() => navigate(`/pbssendoutbatch/${row.id}/${row.username}`)} 
					   style={{borderWidth: 1, borderStyle: 'solid', borderColor: '#7F7F7F', width: '10rem', padding: 10, borderRadius: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: 20, height: '2rem'}}>
						<label style={{cursor: 'pointer'}}>{translations.viewresponse || "View responses"}</label>
					</div>
			)
		}]));
		return createColumnGroup("", viewresponseCol, viewresponseRows);
	};
	
	useEffect(() => {
		if (sendOutBatches) {
			const allColumnGroups = [];
			const sendouts = [...sendOutBatches];

			if (currentSortingColumn) {
				sendouts.sort(sortByKey(currentSortingColumn, sortedAscending));
			}

			const titleColGroup = titleColumnGroup(sendouts);
			const dateColGroup = dateColumnGroup(sendouts);
			const creatorColGroup = creatorColumnGroup(sendouts);
			const viewresponseColGroup = viewresponseColumnGroup(sendouts);
			allColumnGroups.push(titleColGroup);
			allColumnGroups.push(dateColGroup);
			allColumnGroups.push(creatorColGroup);
			allColumnGroups.push(viewresponseColGroup);
			setColumnGroups(allColumnGroups);
		}
	}, [sendOutBatches, sortedAscending, currentSortingColumn, translations, pageNum]);

	useEffect(() => {
		if (!isSendOutsLoading && sendOutsData && timeZone) {
			onGetSendOutBatchesSuccess(sendOutsData, timeZone, filterValue)
		}
	}, [sendOutsData, isSendOutsLoading, timeZone, filterValue])

	useEffect(() => {
		if (error) {
			const modalConf: ModalConfiguration = {
				title: "Error",
				body: <ErrorMessage />,
				buttonText: "Ok",
				type: "error",
				okCallback: () => {
					setError(null)
				}
			}

			openModal(modalConf)
		}
	}, [error])

	const navigateHandler = (num: number) => {
		setPageNum(num)
	}

	const onFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		event.preventDefault()
		setFilterValue(event.target.value)
	}

	if (isSendOutsLoading) {
		return (
			<ComponentWrapper active={2} subMenuActive={2}>
				<div className='spinner-container-global-center'>
					<Spinner />
				</div>
			</ComponentWrapper>
		)
	}

	const dropDown = (
		<li key='dropDown' className='nav-item'>
			<div className='navbar-optionset-container'>
				<div className='navbar-optionset'>
					<OptionSet
						direction='column'
						key={1}
						optionSetId={1}
						options={options}
						selectedOption={filterValue}
						handleChange={onFilterChange}
					/>
				</div>
			</div>
		</li>
	)


	return (
		<ComponentWrapper active={2} subMenuActive={2}>
			<h1 className = 'heading'>{translations.history || "History"}</h1>
			<div style={{ display: 'flex', paddingLeft: 20, paddingRight: 20, marginTop: 20, flexDirection: 'column', width: '90rem' }}>
				<div style={{ width: '80rem' }}>
					<ColumnGroupTable
						columngroups={columnGroups}
						sortedByColumn={currentSortingColumn}
						isSortedAscending={sortedAscending}
						hidePageInputs={true}
						navigateHandler={navigateHandler}
					/>
				</div>
			</div>
		</ComponentWrapper>

	)
}

export default MBSList
